import React, { JSX, useContext, useEffect, useRef, useState } from 'react';
import { CreateMembershipStatus, Pages } from '@lib/types';
import { MembershipAppStateContext } from '../components/MembershipAppState/MembershipAppState';
import { AppHeader } from '@ace-clublabs/shared-component-library';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { CACHED_MEMBERSHIP_STATUS_KEY } from '../utils/constants';
import { PaymentType } from '@ace-clublabs/payment-types';

export interface CreateMembershipStatusProps {}

export const RETRY_INTERVAL = 5000;

export const CreateMembershipStatusPage: (props: CreateMembershipStatusProps) => JSX.Element = (props) => {
  const urlParams = new URLSearchParams(window.location.search);
  const queryId: string | null = urlParams.get('id') ?? null;

  const { checkMembershipStatus, refreshSession, routeMembershipStatus, membershipInfo } =
    useContext(MembershipAppStateContext);
  const [sessionId] = useState<string | null>(queryId);
  const { loadingInfo, loadingProvider } = useContext(MembershipAppStateContext);
  const [pollingStarted, setPollingStarted] = useState(false);

  const maxRetries = 24;
  let retries = 0;

  let timer = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    refreshSession(sessionId);
    timer.current = setInterval(checkStatus, RETRY_INTERVAL);
  }, []);

  useEffect(() => {
    if (loadingProvider && membershipInfo && !pollingStarted) {
      setPollingStarted(true);
    }
  }, [loadingProvider, membershipInfo]);

  useEffect(() => {
    if (pollingStarted) {
      loadingProvider.startLoading('Wrapping up your membership');
    }
  }, [pollingStarted]);

  const finish: (status: string, paymentType?: PaymentType) => void = async (status, paymentType) => {
    if (timer.current != null) {
      clearInterval(timer.current);
    }
    await routeMembershipStatus(status, { paymentType });
  };

  const checkStatus = async () => {
    console.log('Timer execution', retries);
    const currentStatuses = await checkMembershipStatus();
    const cachedStatuses = sessionStorage.getItem(CACHED_MEMBERSHIP_STATUS_KEY);
    const statuses = currentStatuses?.status ?? [];
    const statusesAreTheSame = JSON.stringify(statuses) === cachedStatuses;
    sessionStorage.setItem(CACHED_MEMBERSHIP_STATUS_KEY, JSON.stringify(statuses));

    const latestStatus = statuses.pop();

    retries += 1;
    const softErrorStatuses = [CreateMembershipStatus.CHARGE_DECLINED, CreateMembershipStatus.CARD_NOT_AAA_VISA];
    if (
      latestStatus === CreateMembershipStatus.COMPLETED ||
      latestStatus === CreateMembershipStatus.FAILED ||
      latestStatus === CreateMembershipStatus.ACTIVE_MEMBERSHIP_EXISTS ||
      latestStatus === CreateMembershipStatus.GROUPON_COUPON_EXPIRED ||
      latestStatus === CreateMembershipStatus.GROUPON_COUPON_REDEEMED ||
      latestStatus === CreateMembershipStatus.GROUPON_COUPON_REFUNDED
    ) {
      finish(latestStatus);
    } else if (retries > maxRetries) {
      finish('TIMEOUT');
    } else if (currentStatuses == null || statusesAreTheSame) {
      console.log('polling');
    } else if (softErrorStatuses.includes(latestStatus as CreateMembershipStatus)) {
      const paymentType = currentStatuses?.error?.paymentType;
      finish(latestStatus as string, paymentType);
    }
  };

  return (
    <>
      <AppHeader sx={{ boxShadow: '' }} />
      <LoadingIndicator
        loadingInfo={loadingInfo}
        maxSteps={1}
        currentStep={loadingInfo.loadingDetails.length}
        estimatedTimeInSeconds={45}
      />
    </>
  );
};
